import { Component, Input, SimpleChanges } from '@angular/core';
import { CalendarStatus } from 'app/model/calendar';
import { DashboardCalendarChartStatsOfMonths, DashboardCalendarOption, DashboardCalendarStatsOfMonths } from 'app/model/dashboardCalendar';
import { RangeDatePicker } from 'app/model/rangeDatePicker';
import { DashboardCalendarChartjsBarStatsService } from 'app/shared/services/data/dashboard/dashboard-calendar-chartjs-stats.service';
import { ngbDateToDate } from 'app/shared/utils/dateToNgbDate';
import { ChartDataset, ChartOptions } from 'chart.js';
import { finalize } from 'rxjs/operators';
import { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';

@Component({
  selector: 'app-dashboard-calendar-chartjs-bar-stats',
  templateUrl: './dashboard-calendar-chartjs-bar-stats.component.html',
  styleUrls: ['./dashboard-calendar-chartjs-bar-stats.component.scss']
})
export class DashboardCalendarChartjsBarStatsComponent {
  private fromDate: Date | null
  private toDate: Date | null

  public isLoading: boolean = true

  public data: ChartDataset<'bar'>[]

  public labels: string[]

  public options: ChartOptions<'bar'>

  public isAccepted: boolean
  public isEmpty: boolean

  public chartJsStats: DashboardCalendarChartStatsOfMonths

  @Input() public status: CalendarStatus
  @Input() public selectedDptId: number
  @Input() public selectedEmployeeId: number
  @Input() public calendarOption: DashboardCalendarOption

  @Input() public dateRange: RangeDatePicker

  /**
  * Constructor
  *
  * @param {DashboardCalendarChartjsBarStatsService} dashboardCalendarChartjsBarStatsService
  * 
  */
  constructor(
    private dashboardCalendarChartjsBarStatsService: DashboardCalendarChartjsBarStatsService
  ) { }

  ngOnInit(): void {
    this.initializeValues()
  }

  ngOnChanges(changes: SimpleChanges): void {
    const dateRange = changes['dateRange']
    const status = changes['status']

    if (dateRange && dateRange.currentValue) this.checkIfRangeIsValid(dateRange.currentValue)
    if (status && status.currentValue) this.checkIfItsAccepted(status.currentValue)

    this.getDashboardCalendarStats()
  }

  private initializeValues(): void {
    this.chartJsStats = new DashboardCalendarChartStatsOfMonths()

    this.isEmpty = true

    this.setChartOptions()
  }

  private checkIfRangeIsValid(dateRange: RangeDatePicker): void {
    this.fromDate = ngbDateToDate(dateRange.fromDate)
    this.toDate = ngbDateToDate(dateRange.toDate)
  }

  private checkIfItsAccepted(status: CalendarStatus): void {
    this.isAccepted = status === CalendarStatus.ACCEPTED
  }

  private setChartOptions(): void {
    this.options = this.dashboardCalendarChartjsBarStatsService.createChartOptions()
  }

  private getDashboardCalendarStats(): void {
    this.isLoading = true

    this.dashboardCalendarChartjsBarStatsService.getDashboardCalendarStats(
      this.calendarOption,
      this.selectedDptId,
      this.selectedEmployeeId,
      this.fromDate,
      this.toDate
    )
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (data) => {
          this.getLabels()

          this.mapChartJsStats(data)

          this.mapData()

          this.checkIfItsEmpty()
        },
        error: () => {
          const alertOptions: SweetAlertOptions = {
            title: '¡Error!',
            html: `Ha ocurrido un error al intentar cargar el gráfico mensual. <br><br> Póngase en contacto con un administrador.`,
            icon: 'error',
            customClass: {
              confirmButton: 'btn red-bg text-white ml-1'
            },
          }

          this.fireSwalAlert(alertOptions)
        }
      })
  }

  private getLabels(): void {
    this.labels = this.dashboardCalendarChartjsBarStatsService.getChartJSLabels()
  }

  private mapChartJsStats(stats: DashboardCalendarStatsOfMonths): void {
    this.chartJsStats = this.dashboardCalendarChartjsBarStatsService.mapChartJsStats(stats, this.isAccepted)
  }

  private mapData(): void {
    this.data = this.dashboardCalendarChartjsBarStatsService.mapChartJsData(this.chartJsStats)
  }

  private checkIfItsEmpty(): void {
    this.isEmpty = this.data.every(item => (item.data as number[]).every(data => data === 0))
  }

  private fireSwalAlert(options: SweetAlertOptions): Promise<SweetAlertResult> {
    return this.dashboardCalendarChartjsBarStatsService.fireSwalAlert(options)
  }
}
